/**
 * Papéis do sistema (PRD §2, ADR-008).
 *
 * IMPORTANTE: os predicados deste arquivo são **conveniência de UX** — servem
 * para esconder um botão que o usuário não pode usar. A autorização real vive
 * na RLS e nas RPCs (architecture.md §0, regra 2). Nunca trate uma checagem
 * daqui como garantia de segurança.
 */

export const ROLES = ['admin', 'overseer', 'coordinator', 'leader', 'assistant'] as const;

export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrador',
  /** Renomeado de "pastor" (ADR-008 §5): o papel descreve a capacidade
   *  concedida pelo sistema, não o cargo eclesiástico de quem o exerce. */
  overseer: 'Responsável pastoral',
  coordinator: 'Coordenação',
  leader: 'Liderança',
  assistant: 'Auxiliar',
};

/**
 * Capacidades por papel, para esconder botão que o usuário não pode usar.
 *
 * ⚠️ Isto NÃO é mais transcrição de uma matriz do PRD — ela foi removida de lá
 * (nota de revisão 3ª) justamente porque congelava decisão ainda em formação.
 * O PRD hoje declara só o **raio de alcance** de cada papel (§2); **quais
 * verbos** cada papel tem é decidido por feature, na spec técnica dela.
 *
 * Consequência prática: ao mexer numa entrada daqui, a spec da feature
 * correspondente é quem precisa concordar — não o PRD. E se qualquer um dos
 * dois divergir das políticas de RLS, o banco é a verdade.
 */
const ROLES_BY_ABILITY = {
  /** Ver projetos de todas as filiais */
  readAllBranches: ['admin'],
  /** Ver projetos de toda a própria filial (ADR-008: overseer não é mais global) */
  readOwnBranch: ['admin', 'overseer'],
  /** Ver projetos da própria coordenação */
  readOwnCoordination: ['admin', 'overseer', 'coordinator'],
  /**
   * Criar / editar rascunho de projeto.
   *
   * `overseer` entra aqui (PRD Feature 2, decisão 1): ele cria projeto para
   * qualquer ministério da própria filial. Escrever e VER RASCUNHO ALHEIO são
   * coisas separadas — a segunda ele não tem, e quem decide isso é
   * `my_draft_ministry_ids()` no banco, não este arquivo.
   */
  writeDraft: ['admin', 'overseer', 'leader', 'assistant'],
  /**
   * Submeter projeto para análise.
   *
   * Mesma lista de `writeDraft`, e não uma menor: o PRD (Feature 2, decisão 2)
   * é explícito de que `leader` e `assistant` têm hoje as mesmas capacidades, e
   * a spec do formulário removeu a `can_submit_for_ministry()` que separava os
   * dois — quem autoriza submissão no banco é `can_write_ministry`. Uma lista
   * mais estreita aqui esconderia um botão que a RPC aceita, que é a pior
   * combinação possível: o voluntário não vê o caminho que existe.
   */
  submitForReview: ['admin', 'overseer', 'leader', 'assistant'],
  /** Analisar e confirmar projeto (da própria filial, no caso de overseer) */
  reviewProject: ['admin', 'overseer'],
  /** Registrar atualização de andamento */
  postProgressUpdate: ['admin', 'leader', 'assistant'],
  /** Ver calendário consolidado */
  viewCalendar: [...ROLES],
} as const satisfies Record<string, readonly Role[]>;

export type Ability = keyof typeof ROLES_BY_ABILITY;

/** Verdadeiro se **algum** dos papéis vigentes da pessoa concede a ação. */
export function can(roles: readonly Role[], ability: Ability): boolean {
  const allowed: readonly Role[] = ROLES_BY_ABILITY[ability];
  return roles.some((role) => allowed.includes(role));
}

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}
